/** Place the marker freely inside a circle whose outer range is adjustable. */

import { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { ContextCloseBar, contextPanelPosition } from './ContextPanel';


const SIZE = 180;
const C = SIZE / 2;
const R_MAX = 72;

function at(dir, radius) {
  const angle = dir * Math.PI / 4;
  return [C + radius * Math.sin(angle), C - radius * Math.cos(angle)];
}

const btnBase = {
  fontSize: '11px', fontWeight: '700', padding: '7px 10px', borderRadius: '6px',
  border: '1px solid transparent', cursor: 'pointer',
  // A thumb, not a cursor — the same floor the item menu and the toast buttons
  // settled on. This panel is the mark's whole control surface.
  minHeight: 32,
  // Same rule as the item menu: the panel is capped so it never covers the
  // pitch, so a long label WRAPS instead of running off the edge.
  whiteSpace: 'normal', overflowWrap: 'break-word', textAlign: 'center', lineHeight: '1.3',
};

export default function MarkPickerModal({
  title, hint, dir: initialDir = 0, dist: initialDist = 1.5,
  minDist = 0.5, maxDist = 20, step = 0.5,
  confirmLabel, altLabel, onAlt, releaseLabel, onRelease, onCommit, onCancel,
  // ✕ Remove the mark KEY itself (as opposed to "stop marking", which writes a
  // release key and keeps the marker where they got to). Offered only when a key
  // really is sitting on this instant — see App, where the dial is opened.
  deleteLabel = null, onDelete = null,
  bottom = 0,
  // (dir, dist) — every time the dial moves, including the first paint. The
  // caller draws the spot on the pitch; the dial stays the thing you touch.
  onPreview = null,
}) {
  const { t } = useI18n();
  const [dir, setDirRaw] = useState(initialDir);
  const [dist, setDistRaw] = useState(initialDist);
  const [range, setRange] = useState(() => Math.min(maxDist, Math.max(2, initialDist)));
  const update = (d, gap) => {
    setDirRaw(d); setDistRaw(gap);
    if (onPreview) onPreview(d, gap);
  };
  useEffect(() => {
    setDirRaw(initialDir); setDistRaw(initialDist);
    setRange(r => Math.min(maxDist, Math.max(r, initialDist)));
    if (onPreview) onPreview(initialDir, initialDist);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialDir, initialDist]);
  const [dx, dy] = at(dir, R_MAX * dist / range);
  const place = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) * SIZE / rect.width - C;
    const y = (e.clientY - rect.top) * SIZE / rect.height - C;
    const radius = Math.hypot(x, y);
    const bearing = radius > 1e-6 ? ((Math.atan2(x, -y) * 4 / Math.PI) + 8) % 8 : dir;
    update(bearing, Math.max(minDist, Math.min(range, radius / R_MAX * range)));
  };
  const keyboardPlace = e => {
    const directions = { ArrowLeft: -1 / 45, ArrowRight: 1 / 45 };
    if (e.key in directions) {
      e.preventDefault(); e.stopPropagation(); update((dir + directions[e.key] + 8) % 8, dist);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault(); e.stopPropagation();
      update(dir, Math.max(minDist, Math.min(range, dist + (e.key === 'ArrowUp' ? 0.1 : -0.1))));
    }
  };
  return (
    <div
      className="kv-context-panel kv-mark-panel"
      role="group" aria-label={title} aria-description={hint || undefined}
      style={{
        ...contextPanelPosition(bottom), zIndex: 250,
        background: 'rgba(15, 23, 42, 0.97)', border: '1px solid #a78bfa',
        borderRadius: '10px', boxShadow: '0 12px 34px rgba(0,0,0,0.65)',
        padding: 0, width: 'max-content', boxSizing: 'border-box',
        maxWidth: 'min(200px, calc(100vw - 16px))',
        display: 'flex', flexDirection: 'column', textAlign: 'center',
      }}
    >
      <ContextCloseBar label={t('fb.close')} onClick={onCancel} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
      <div title={title} style={{ fontSize: 12, fontWeight: 800, color: '#f8fafc', textAlign: 'left' }}>{t('mark.positionTitle')}</div>

      <svg width={160} height={160} viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="group" tabIndex={0} aria-label={t('mark.placeAria')}
        aria-description={`${(dir * 45).toFixed(0)}°, ${dist.toFixed(1)} m`}
        onKeyDown={keyboardPlace}
        onPointerDown={e => {
          if (e.button !== 0) return;
          e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); place(e);
        }}
        onPointerMove={e => { if (e.currentTarget.hasPointerCapture?.(e.pointerId)) place(e); }}
        onPointerUp={e => { if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); }}
        style={{ alignSelf: 'center', touchAction: 'none', cursor: 'crosshair' }}>
        <circle cx={C} cy={C} r={R_MAX} fill="rgba(148,163,184,0.07)" stroke="#a78bfa" />
        <circle cx={C} cy={C} r={R_MAX / 2} fill="none" stroke="#334155" strokeDasharray="3 4" />
        <line x1={C - R_MAX} y1={C} x2={C + R_MAX} y2={C} stroke="#334155" />
        <line x1={C} y1={C - R_MAX} x2={C} y2={C + R_MAX} stroke="#334155" />
        <line x1={C} y1={C} x2={dx} y2={dy} stroke="#a78bfa" strokeWidth="1.5" />
        <circle cx={C} cy={C} r="6" fill="#dc2626" stroke="#fecaca" />
        <circle data-testid="mark-position" cx={dx} cy={dy} r="7" fill="#2563eb" stroke="#bfdbfe" strokeWidth="1.5" />
      </svg>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', color: '#cbd5e1' }}>
        <input type="range" min={minDist} max={maxDist} step={step} value={range}
          onChange={e => {
            const next = Number(e.target.value); setRange(next);
            if (dist > next) update(dir, next);
          }}
          style={{ flex: 1, accentColor: '#a78bfa', minWidth: 90 }}
          aria-label={t('mark.rangeAria')}
        />
        <span style={{ fontWeight: '800', color: '#f8fafc', minWidth: '44px' }}>{range.toFixed(1)} m</span>
      </label>
      <div aria-live="polite" aria-label={t('mark.distAria')} style={{ fontSize: '11px', color: '#cbd5e1' }}>
        {dist.toFixed(1)} m · {(dir * 45).toFixed(0)}°
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button type="button" onClick={() => onCommit(dir, dist)}
          style={{ ...btnBase, flex: 1, background: '#5b21b6', color: '#ede9fe' }}>
          {confirmLabel}
        </button>
      </div>

      {/* The rarer edits, kept out of the way of the common one: the dial opened
          because they already mark someone, and the coach wants a different
          player — or no player at all. Text buttons, not third and fourth
          coloured blocks: this panel is the mark's whole control surface.

          Every one of these is passed only when it can actually be done. A link
          that closes the panel and changes nothing is worse than no link: the
          coach learns that this panel lies. */}
      {(onAlt || onRelease || onDelete) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          {onAlt && altLabel && (
            <button type="button" onClick={onAlt}
              style={{
                ...btnBase, background: 'transparent', color: '#93c5fd',
                border: 'none', fontSize: '11px', padding: '2px 8px',
              }}>
              {altLabel}
            </button>
          )}
          {onRelease && releaseLabel && (
            <button type="button" onClick={onRelease}
              style={{
                ...btnBase, background: 'transparent', color: '#fca5a5',
                border: 'none', fontSize: '11px', padding: '2px 8px',
              }}>
              {releaseLabel}
            </button>
          )}
          {onDelete && deleteLabel && (
            <button type="button" onClick={onDelete}
              style={{
                ...btnBase, background: 'transparent', color: '#fca5a5',
                border: 'none', fontSize: '11px', padding: '2px 8px',
              }}>
              {deleteLabel}
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
